/**
 * Time-on-view ("dwell") timers for usage analytics.
 *
 * A timer runs only while its view is open AND the page is visible. When the
 * page is hidden the time so far is reported and the timer pauses, so closing
 * the tab loses nothing and a background tab counts nothing. One visit can
 * therefore be reported as several dwell events; the dashboard sums them.
 */

import { onPageHide, track, type EventProps } from "./queue";

interface Timer {
  event: string;
  props: EventProps;
  since: number | null;
  seconds: number;
}

// Shorter than this is a click-through, not a view.
const MIN_SECONDS = 1;

const timers = new Map<string, Timer>();

function visible(): boolean {
  return document.visibilityState === "visible";
}

function report(t: Timer): void {
  if (t.since !== null) {
    t.seconds += (Date.now() - t.since) / 1000;
    t.since = null;
  }
  if (t.seconds >= MIN_SECONDS) {
    track(t.event, { ...t.props, seconds: Math.round(t.seconds) });
  }
  t.seconds = 0;
}

/** Make the set of running timers match `wanted` (key → event + props). */
export function syncDwell(wanted: Map<string, { event: string; props: EventProps }>): void {
  for (const [key, t] of timers) {
    if (!wanted.has(key)) {
      report(t);
      timers.delete(key);
    }
  }
  for (const [key, w] of wanted) {
    if (!timers.has(key)) {
      timers.set(key, {
        event: w.event,
        props: w.props,
        since: visible() ? Date.now() : null,
        seconds: 0,
      });
    }
  }
}

onPageHide(() => {
  for (const t of timers.values()) report(t);
});

document.addEventListener("visibilitychange", () => {
  if (!visible()) return;
  const now = Date.now();
  for (const t of timers.values()) if (t.since === null) t.since = now;
});
